import React, { useState, useEffect } from 'react';

const Claims = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    // AOS Animation initialization
    const elements = document.querySelectorAll('[data-aos]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aos-animate');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    elements.forEach(el => observer.observe(el));

    // Back to Top Button functionality
    const backToTop = document.getElementById("backToTop");
    if (backToTop) {
      window.onscroll = function () {
        if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
          backToTop.style.display = "block";
        } else {
          backToTop.style.display = "none";
        }
      };

      backToTop.addEventListener("click", () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }, []);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqData = [
    {
      question: "How long does it take to process a claim?",
      answer: "Most claims are processed within 3-7 working days after all required documents are submitted. Emergency claims and cashless treatments are processed within 24-48 hours."
    },
    {
      question: "Can I submit documents online?",
      answer: "Yes, you can upload all required documents through our online portal or mobile app. This speeds up the processing time significantly compared to physical submission."
    },
    {
      question: "What is cashless treatment facility?",
      answer: "Cashless treatment allows you to get medical treatment at our network hospitals without paying upfront. We settle the bills directly with the hospital after pre-authorization."
    },
    {
      question: "What if my claim is rejected?",
      answer: "If your claim is rejected, you'll receive a detailed explanation. You can appeal the decision by providing additional documentation or contacting our customer service team for clarification."
    },
    {
      question: "Can I track my claim status online?",
      answer: "Yes, you can track your claim status in real-time using our online portal, mobile app, or the tracking tool on this page. You'll also receive SMS and email updates."
    },
    {
      question: "Is there a time limit to file a claim?",
      answer: "Yes, claims should be intimated within 24 hours of the incident and documents submitted within 30 days. However, we may consider claims beyond this period based on valid reasons."
    }
  ];

  return (
    <>
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Claims Process</h1>
          <p>Simple, fast, and hassle-free claim settlement process</p>
        </div>
      </section>

      {/* Claims Process Steps */}
      <section className="claims-process">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2 className="section-title">How to File a Claim</h2>
            <p className="section-subtitle">
              Follow these simple steps for quick claim settlement
            </p>
          </div>

          <div className="process-steps">
            <div className="step-card" data-aos="fade-up">
              <div className="step-number">1</div>
              <div className="step-icon">
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 16.92V19.92C22 20.52 21.52 21 20.92 21C9.4 21 0 11.6 0 0.08C0 -0.52 0.48 -1 1.08 -1H4.08C4.68 -1 5.16 -0.52 5.16 0.08C5.16 1.68 5.44 3.24 5.96 4.68C6.08 5 5.96 5.36 5.68 5.6L3.44 7.84C5.28 11.48 8.52 14.72 12.16 16.56L14.4 14.32C14.64 14.04 15 13.92 15.32 14.04C16.76 14.56 18.32 14.84 19.92 14.84C20.52 14.84 21 15.32 21 15.92V18.92C21 19.52 20.52 20 19.92 20Z"
                    fill="#0057A4"
                  />
                </svg>
              </div>
              <h3>Immediate Intimation</h3>
              <p>
                Call our helpline immediately after the incident. Our team will
                guide you through the initial process.
              </p>
              <div className="step-details">
                <strong>Within:</strong> 24 hours of incident<br />
                <strong>Required:</strong> Policy number, incident details
              </div>
            </div>

            <div className="step-card" data-aos="fade-up" data-aos-delay="100">
              <div className="step-number">2</div>
              <div className="step-icon">
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <polyline
                    points="14,2 14,8 20,8"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <line
                    x1="16"
                    y1="13"
                    x2="8"
                    y2="13"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <line
                    x1="16"
                    y1="17"
                    x2="8"
                    y2="17"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>Document Submission</h3>
              <p>
                Submit the required documents online or visit our nearest branch.
                Our team will help you prepare the complete documentation.
              </p>
              <div className="step-details">
                <strong>Mode:</strong> Online upload or branch visit<br />
                <strong>Time:</strong> Within 7 days of intimation
              </div>
            </div>

            <div className="step-card" data-aos="fade-up" data-aos-delay="200">
              <div className="step-number">3</div>
              <div className="step-icon">
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11L12 14L22 4" stroke="#0057A4" strokeWidth="2" />
                  <path
                    d="M21 12V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V5C3 3.9 3.9 3 5 3H16"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>Claim Assessment</h3>
              <p>
                Our expert assessors will evaluate your claim. For health claims,
                we work directly with hospitals for cashless treatment.
              </p>
              <div className="step-details">
                <strong>Timeline:</strong> 48-72 hours<br />
                <strong>Updates:</strong> SMS and email notifications
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="documents-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2 className="section-title">Required Documents</h2>
            <p className="section-subtitle">
              Keep these documents ready for faster claim processing
            </p>
          </div>

          <div className="documents-grid">
            {/* Life Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="100">
              <h3>Life Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Death certificate</li>
                <li>✓ Medical treatment records</li>
                <li>✓ Post-mortem report (if applicable)</li>
                <li>✓ Nominee ID proof</li>
                <li>✓ Bank account details</li>
                <li>✓ Legal heir certificate (if nominee not mentioned)</li>
              </ul>
            </div>

            {/* Health Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="200">
              <h3>Health Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Hospital discharge summary</li>
                <li>✓ Original medical bills & receipts</li>
                <li>✓ Diagnostic reports</li>
                <li>✓ Doctor's prescription</li>
                <li>✓ Bank account details</li>
                <li>✓ ID proof (Aadhaar/PAN)</li>
              </ul>
            </div>

            {/* Motor Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="300">
              <h3>Motor Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ FIR copy (for theft / accident cases)</li>
                <li>✓ RC book copy</li>
                <li>✓ Driving license copy</li>
                <li>✓ Estimate of repairs</li>
                <li>✓ Photos of damaged vehicle</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Travel Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="400">
              <h3>Travel Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Passport copy</li>
                <li>✓ Medical bills & reports (if applicable)</li>
                <li>✓ Flight delay/cancellation certificate</li>
                <li>✓ Baggage delay/loss report</li>
                <li>✓ Original invoices/receipts</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Commercial Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="500">
              <h3>Commercial Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Proof of loss/damage</li>
                <li>✓ Purchase invoices of damaged items</li>
                <li>✓ Stock register (if applicable)</li>
                <li>✓ Fire brigade/police report (if applicable)</li>
                <li>✓ Bank account details</li>
                <li>✓ ID & business proof</li>
              </ul>
            </div>

            {/* Liability Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="600">
              <h3>Liability Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Incident/accident report</li>
                <li>✓ Third-party claim documents</li>
                <li>✓ Legal notice (if applicable)</li>
                <li>✓ FIR copy (if applicable)</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Marine / Transit Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="700">
              <h3>Marine / Transit Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Bill of lading / Consignment note</li>
                <li>✓ Invoice & packing list</li>
                <li>✓ Damage/Loss certificate from carrier</li>
                <li>✓ Survey report</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Employee Benefit Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="800">
              <h3>Employee Benefit Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Employee ID / Employment proof</li>
                <li>✓ Hospital/medical records (for health cover)</li>
                <li>✓ Salary slips (if applicable)</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Group Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="900">
              <h3>Group Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Employer certificate</li>
                <li>✓ Employee ID proof</li>
                <li>✓ Hospital/medical bills (for group health)</li>
                <li>✓ Death certificate (for group life)</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Corporate Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="1000">
              <h3>Corporate Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Certificate of incorporation / Business proof</li>
                <li>✓ Claim supporting documents (bills, reports)</li>
                <li>✓ Legal documents (if liability cases)</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Director & Professional Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="1100">
              <h3>Director & Professional Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Professional license/registration copy</li>
                <li>✓ Incident/accident report</li>
                <li>✓ FIR copy (if applicable)</li>
                <li>✓ Legal notice / Court documents (if applicable)</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Cyber Insurance */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="1200">
              <h3>Cyber Insurance Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy copy</li>
                <li>✓ Incident / cyber-attack report</li>
                <li>✓ IT forensic investigation report</li>
                <li>✓ Evidence of data breach / system compromise</li>
                <li>✓ Legal notice / regulatory communication (if applicable)</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>

            {/* Surety Bonds */}
            <div className="document-category" data-aos="fade-up" data-aos-delay="1300">
              <h3>Surety Bonds Claims</h3>
              <ul className="document-list">
                <li>✓ Duly filled claim form</li>
                <li>✓ Policy / Bond copy</li>
                <li>✓ Contract agreement copy</li>
                <li>✓ Proof of non-performance / breach</li>
                <li>✓ Demand letter / claim intimation</li>
                <li>✓ Supporting documents for financial loss</li>
                <li>✓ Bank account details</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="faqs-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Common questions about the claims process
            </p>
          </div>

          <div className="faq-container">
            {faqData.map((faq, index) => (
              <div key={index} className="faq-item" data-aos="fade-up" data-aos-delay={index * 100}>
                <div 
                  className="faq-question" 
                  onClick={() => toggleFAQ(index)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    marginBottom: openFAQ === index ? '0' : '0rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <h3 style={{ margin: '0', fontSize: '1.1rem', fontWeight: '600' }}>
                    {faq.question}
                  </h3>
                  <span 
                    className="faq-toggle"
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: '#0057A4',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    {openFAQ === index ? '−' : '+'}
                  </span>
                </div>
                <div 
                  className="faq-answer" 
                  style={{
                    display: openFAQ === index ? 'block' : 'none',
                    padding: openFAQ === index ? '1.5rem' : '0',
                    backgroundColor: '#ffffff',
                    border: openFAQ === index ? '1px solid #e9ecef' : 'none',
                    borderTop: 'none',
                    borderRadius: openFAQ === index ? '0 0 8px 8px' : '0',
                    marginBottom: openFAQ === index ? '0rem' : '0',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden'
                  }}
                >
                  <p style={{
                    margin: '0',
                    color: '#6c757d',
                    lineHeight: '1.6',
                    fontSize: '0.95rem'
                  }}>
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button id="backToTop" title="Go to top">↑</button>
    </>
  );
};

export default Claims;
